/**
 * Document Manager
 * Handles storage and retrieval of document metadata for properties, owners, and clients
 */

const STORAGE_KEYS = {
  property: 'property_documents',
  owner: 'owner_documents',
  client: 'client_documents'
};

/**
 * Get documents for a specific entity
 * @param {string} entityType - Type of entity (property, owner, client)
 * @param {string} entityId - ID of the entity
 * @returns {Array} Array of document objects
 */
export const getDocuments = (entityType, entityId) => {
  try {
    const storageKey = STORAGE_KEYS?.[entityType];
    if (!storageKey) return [];

    const stored = localStorage?.getItem(storageKey);
    const allDocuments = stored ? JSON?.parse(stored) : {};
    return allDocuments?.[entityId] || [];
  } catch (error) {
    console.error('Error loading documents:', error);
    return [];
  }
};

/**
 * Save documents for a specific entity
 * @param {string} entityType - Type of entity (property, owner, client)
 * @param {string} entityId - ID of the entity
 * @param {Array} documents - Array of document objects
 * @returns {boolean} Success status
 */
export const saveDocuments = (entityType, entityId, documents) => {
  try {
    const storageKey = STORAGE_KEYS?.[entityType];
    if (!storageKey) return false;

    const stored = localStorage?.getItem(storageKey);
    const allDocuments = stored ? JSON?.parse(stored) : {};
    
    allDocuments[entityId] = documents;
    localStorage?.setItem(storageKey, JSON?.stringify(allDocuments));
    return true;
  } catch (error) {
    console.error('Error saving documents:', error);
    return false;
  }
};

/**
 * Get all documents across all entities of a type
 * @param {string} entityType - Type of entity (property, owner, client)
 * @returns {Object} Object with entityId as keys and document arrays as values
 */
export const getAllDocuments = (entityType) => {
  try {
    const storageKey = STORAGE_KEYS?.[entityType];
    if (!storageKey) return {};

    const stored = localStorage?.getItem(storageKey);
    return stored ? JSON?.parse(stored) : {};
  } catch (error) {
    console.error('Error loading all documents:', error);
    return {};
  }
};

/**
 * Delete a specific document
 * @param {string} entityType - Type of entity
 * @param {string} entityId - ID of the entity
 * @param {string} documentId - ID of the document to delete
 * @returns {boolean} Success status
 */
export const deleteDocument = (entityType, entityId, documentId) => {
  try {
    const documents = getDocuments(entityType, entityId);
    const updatedDocuments = documents?.filter(doc => doc?.id !== documentId);
    return saveDocuments(entityType, entityId, updatedDocuments);
  } catch (error) {
    console.error('Error deleting document:', error);
    return false;
  }
};

/**
 * Get document count for an entity
 * @param {string} entityType - Type of entity
 * @param {string} entityId - ID of the entity
 * @returns {number} Number of documents
 */
export const getDocumentCount = (entityType, entityId) => {
  const documents = getDocuments(entityType, entityId);
  return documents?.length || 0;
};

/**
 * Search documents by name or category
 * @param {string} entityType - Type of entity
 * @param {string} entityId - ID of the entity
 * @param {string} searchTerm - Search term
 * @returns {Array} Filtered array of documents
 */
export const searchDocuments = (entityType, entityId, searchTerm) => {
  const documents = getDocuments(entityType, entityId);
  const term = searchTerm?.toLowerCase();
  
  return documents?.filter(doc => 
    doc?.name?.toLowerCase()?.includes(term) ||
    doc?.category?.toLowerCase()?.includes(term)
  );
};